'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export interface CartItemData {
  id: string;
  name: string;
  image: string;
  alt: string;
  packPrice: number;
  boxPrice: number;
  packQuantity: number;
  boxQuantity: number;
  itemsPerPack: number;
  packsPerBox: number;
  minPackQuantity: number;
  minBoxQuantity: number;
  category: string;
}

interface CartItemProps {
  item: CartItemData;
  onUpdateQuantity: (id: string, type: 'pack' | 'box', quantity: number) => void;
  onRemove: (id: string) => void;
}

const CartItem = ({ item, onUpdateQuantity, onRemove }: CartItemProps) => {
  const [isRemoving, setIsRemoving] = useState(false);

  const handleQuantityChange = (type: 'pack' | 'box', delta: number) => {
    const currentQuantity = type === 'pack' ? item.packQuantity : item.boxQuantity;
    const minQuantity = type === 'pack' ? item.minPackQuantity : item.minBoxQuantity;
    const newQuantity = Math.max(minQuantity, currentQuantity + delta);
    onUpdateQuantity(item.id, type, newQuantity);
  };

  const handleRemove = () => {
    setIsRemoving(true);
    setTimeout(() => {
      onRemove(item.id);
    }, 250);
  };

  const packTotal = item.packPrice * item.packQuantity;
  const boxTotal = item.boxPrice * item.boxQuantity;
  const itemTotal = packTotal + boxTotal;

  return (
    <div
      className={`overflow-hidden rounded-lg bg-card shadow-sm transition-all duration-250 ${
        isRemoving ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
      }`}
    >
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:gap-6">
        {/* Product Image */}
        <div className="relative h-32 w-full flex-shrink-0 overflow-hidden rounded-md sm:h-40 sm:w-40">
          <AppImage
            src={item.image}
            alt={item.alt}
            className="h-full w-full object-cover"
          />
        </div>

        {/* Product Details */}
        <div className="flex flex-1 flex-col gap-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="mb-1 font-heading text-lg font-semibold text-text-primary">
                {item.name}
              </h3>
              <p className="font-caption text-sm text-text-secondary">{item.category}</p>
            </div>
            <button
              onClick={handleRemove}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-text-secondary transition-all duration-250 hover:bg-error/10 hover:text-error focus:outline-none focus:ring-2 focus:ring-error focus:ring-offset-2"
              aria-label="Удалить товар"
            >
              <Icon name="TrashIcon" size={20} />
            </button>
          </div>

          {/* Pricing Sections */}
          <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
            {/* Pack Pricing */}
            {item.packQuantity > 0 && (
              <div className="flex-1">
                <div className="mb-2 flex items-center gap-2">
                  <Icon name="CubeIcon" size={16} className="text-text-secondary" />
                  <span className="font-caption text-sm font-medium text-text-primary">
                    Упаковка ({item.itemsPerPack} шт)
                  </span>
                </div>
                <div className="mb-2 flex items-center gap-2">
                  <span className="font-caption text-sm text-text-secondary">Цена:</span>
                  <span className="font-heading text-base font-semibold text-text-primary">
                    {item.packPrice.toLocaleString('ru-RU')} ₸
                  </span>
                </div>
                <div className="mb-3 flex items-center gap-3">
                  <button
                    onClick={() => handleQuantityChange('pack', -1)}
                    disabled={item.packQuantity <= item.minPackQuantity}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-text-primary transition-all duration-250 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                    aria-label="Уменьшить количество упаковок"
                  >
                    <Icon name="MinusIcon" size={16} />
                  </button>
                  <input
                    type="number"
                    value={item.packQuantity}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || item.minPackQuantity;
                      onUpdateQuantity(item.id, 'pack', Math.max(item.minPackQuantity, value));
                    }}
                    className="h-8 w-16 rounded-md border border-border bg-background px-2 text-center font-heading text-sm font-medium text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                    min={item.minPackQuantity}
                  />
                  <button
                    onClick={() => handleQuantityChange('pack', 1)}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-text-primary transition-all duration-250 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                    aria-label="Увеличить количество упаковок"
                  >
                    <Icon name="PlusIcon" size={16} />
                  </button>
                </div>
                {item.packQuantity < item.minPackQuantity && (
                  <p className="font-caption text-xs text-warning">
                    Минимум: {item.minPackQuantity} упаковок
                  </p>
                )}
                <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
                  <span className="font-caption text-sm text-text-secondary">Итого:</span>
                  <span className="font-heading text-base font-semibold text-primary">
                    {packTotal.toLocaleString('ru-RU')} ₸
                  </span>
                </div>
              </div>
            )}

            {/* Box Pricing */}
            {item.boxQuantity > 0 && (
              <div className="flex-1">
                <div className="mb-2 flex items-center gap-2">
                  <Icon name="CubeTransparentIcon" size={16} className="text-text-secondary" />
                  <span className="font-caption text-sm font-medium text-text-primary">
                    Коробка ({item.packsPerBox} упаковок)
                  </span>
                </div>
                <div className="mb-2 flex items-center gap-2">
                  <span className="font-caption text-sm text-text-secondary">Цена:</span>
                  <span className="font-heading text-base font-semibold text-text-primary">
                    {item.boxPrice.toLocaleString('ru-RU')} ₸
                  </span>
                </div>
                <div className="mb-3 flex items-center gap-3">
                  <button
                    onClick={() => handleQuantityChange('box', -1)}
                    disabled={item.boxQuantity <= item.minBoxQuantity}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-text-primary transition-all duration-250 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                    aria-label="Уменьшить количество коробок"
                  >
                    <Icon name="MinusIcon" size={16} />
                  </button>
                  <input
                    type="number"
                    value={item.boxQuantity}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || item.minBoxQuantity;
                      onUpdateQuantity(item.id, 'box', Math.max(item.minBoxQuantity, value));
                    }}
                    className="h-8 w-16 rounded-md border border-border bg-background px-2 text-center font-heading text-sm font-medium text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                    min={item.minBoxQuantity}
                  />
                  <button
                    onClick={() => handleQuantityChange('box', 1)}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-text-primary transition-all duration-250 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                    aria-label="Увеличить количество коробок"
                  >
                    <Icon name="PlusIcon" size={16} />
                  </button>
                </div>
                {item.boxQuantity < item.minBoxQuantity && (
                  <p className="font-caption text-xs text-warning">
                    Минимум: {item.minBoxQuantity} коробок
                  </p>
                )}
                <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
                  <span className="font-caption text-sm text-text-secondary">Итого:</span>
                  <span className="font-heading text-base font-semibold text-primary">
                    {boxTotal.toLocaleString('ru-RU')} ₸
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Item Total */}
          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="font-heading text-base font-medium text-text-primary">
              Общая сумма товара:
            </span>
            <span className="font-heading text-xl font-bold text-accent">
              {itemTotal.toLocaleString('ru-RU')} ₸
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;